import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { unifiedRolesEntity } from '@/config/entities/unifiedRoles';
import { unifiedRolesCRUDService } from '@/services/unifiedRolesCRUDService';
import { UnifiedRole } from '@/services/unifiedRBACService';
import { RoleEditForm } from './RoleEditForm';
import { toast } from 'react-hot-toast';

export const RoleManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState<UnifiedRole | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  const handleCreate = () => {
    setEditingRole(null);
    setShowForm(true);
  };

  const handleEdit = async (item: any) => {
    const role = item as UnifiedRole;
    try {
      // Fetch complete role data including permissions
      const completeRole = await unifiedRolesCRUDService.getById(role.id);
      if (completeRole) {
        setEditingRole(completeRole);
        setShowForm(true);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to load role details';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (item: any) => {
    const role = item as UnifiedRole;
    
    // Add confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete the role "${role.display_name}"? This action cannot be undone.`
    );
    
    if (!confirmed) {
      return;
    }
    
    try {
      await unifiedRolesCRUDService.delete(role.id);
      toast.success('Role deleted successfully');
    } catch (error: any) {
      // Show more specific error message from backend
      const errorMessage = error.response?.data?.message || 'Failed to delete role';
      toast.error(errorMessage);
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      // Extract only the role fields, exclude permissions array to avoid circular reference
      const { permissions, ...roleData } = data;
      
      // Normalize role_type and scope_type to uppercase
      if (roleData.role_type) {
        roleData.role_type = String(roleData.role_type).toUpperCase().trim();
      }
      if (roleData.scope_type) {
        roleData.scope_type = String(roleData.scope_type).toUpperCase().trim();
      }
      
      if (editingRole) {
        // Update existing role
        const updated = await unifiedRolesCRUDService.update(editingRole.id, roleData);
        toast.success('Role updated successfully');
        // Keep form open so admin can continue editing permissions by module
        setEditingRole(updated ?? { ...editingRole, ...roleData });
      } else {
        // Create new role
        await unifiedRolesCRUDService.create(roleData);
        toast.success('Role created successfully');
        // Close form and return to list view
        setShowForm(false);
        setEditingRole(null);
      }
    } catch (error: any) {
      // Extract error message from response
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to save role';
      
      // Show specific error message to user
      toast.error(errorMessage);
      
      // Keep form open on error so user can fix and retry
      // Don't close the form or clear editingRole
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingRole(null);
  };

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedRoleIds(selectedIds);
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--color-surface-text)]">{t('pages.roleManagement')}</h1>
        <p className="text-[var(--color-secondary-text)] mt-2">
          {t('rbac.roleManagement.description')}
        </p>
      </div>



      {showForm ? (
        <RoleEditForm
          role={editingRole}
          onSave={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      ) : (
        <>
          {/* Selection Summary */}
          {selectedRoleIds.length > 0 && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedRoleIds.length} role(s) selected
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setSelectedRoleIds([])}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear selection
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <FlexibleCRUDTable
            entity={unifiedRolesEntity}
            service={unifiedRolesCRUDService}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCreate={handleCreate}
            enableRowSelection={true}
            onSelectionChange={handleSelectionChange}
          />
        </>
      )}
    </div>
  );
};

export default RoleManagementPage;
